'use client'

import { useCallback, useEffect, useId, useRef, useState } from 'react'
import Link from '../../components/framework/link'
import { ChevronDown, Menu, X } from 'lucide-react'
import React from 'react'
import { BrandLogo } from './brand-logo'
import { ACTIONS, NAV_BREAKPOINT, PRIMARY_NAV } from '../../config/site'
import { AccountMenu } from './account-menu'

/**
 * The only interactive part of the header, and the only client component in
 * the layout.
 *
 * The drawer stays mounted and is moved off-canvas with a transform so the
 * slide can be a plain CSS transition. `inert` keeps the closed drawer out of
 * the tab order and the accessibility tree, which a transform alone would not.
 */
export function MobileNav() {
  const [open, setOpen] = useState(false)
  const drawerId = useId()
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const panelRef = useRef<HTMLDivElement | null>(null)
  const closeRef = useRef<HTMLButtonElement | null>(null)

  /** Every dismissal path returns focus to the button that opened the drawer. */
  const close = useCallback(() => {
    setOpen(false)
    triggerRef.current?.focus()
  }, [])

  useEffect(() => {
    if (!open) return

    closeRef.current?.focus()

    // Lock the page behind the drawer, remembering whatever was there before.
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault()
        close()
        return
      }

      // The page behind stays in the DOM, so Tab is cycled inside the panel.
      if (event.key !== 'Tab') return

      const panel = panelRef.current
      if (!panel) return

      const focusable = panel.querySelectorAll<HTMLElement>('a[href], button:not([disabled])')
      if (focusable.length === 0) return

      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      const active = document.activeElement
      const inside = active instanceof Node && panel.contains(active)

      if (event.shiftKey && (!inside || active === first)) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && (!inside || active === last)) {
        event.preventDefault()
        first.focus()
      }
    }

    // Widening past the nav breakpoint hides the drawer with CSS. Without this
    // the component would still think it was open and the body would stay
    // scroll-locked with nothing on screen to close.
    const desktop = window.matchMedia(NAV_BREAKPOINT)
    function onBreakpointChange() {
      if (desktop.matches) setOpen(false)
    }

    document.addEventListener('keydown', onKeyDown)
    desktop.addEventListener('change', onBreakpointChange)

    return () => {
      document.removeEventListener('keydown', onKeyDown)
      desktop.removeEventListener('change', onBreakpointChange)
      document.body.style.overflow = previousOverflow
    }
  }, [open, close])

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(true)}
        aria-expanded={open}
        aria-controls={drawerId}
        aria-label="Open main menu"
        className="inline-flex size-11 items-center justify-center rounded-btn text-ink motion-micro hover:bg-soft nav:hidden"
      >
        <Menu className="size-6" aria-hidden="true" />
      </button>

      {/* `overflow-hidden` clips the off-canvas panel so it can never widen the
          page. Pointer events are dropped while closed so the invisible layer
          does not swallow clicks. */}
      <div
        className={`fixed inset-0 z-100 overflow-hidden nav:hidden ${open ? '' : 'pointer-events-none'}`}
        inert={open ? undefined : ''}
      >
        <div
          onClick={close}
          aria-hidden="true"
          className={`absolute inset-0 bg-navy/50 transition-opacity duration-300 ease-out motion-reduce:transition-none ${
            open ? 'opacity-100' : 'opacity-0'
          }`}
        />

        <div
          ref={panelRef}
          id={drawerId}
          role="dialog"
          aria-modal="true"
          aria-label="Main menu"
          className={`absolute inset-y-0 right-0 flex w-[min(88vw,380px)] flex-col bg-white shadow-drawer motion-arrow motion-reduce:transition-none ${
            open ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <div className="flex h-18 shrink-0 items-center justify-between border-b border-line px-5">
            <BrandLogo size="sm" />
            <button
              ref={closeRef}
              type="button"
              onClick={close}
              aria-label="Close main menu"
              className="inline-flex size-11 items-center justify-center rounded-btn text-ink motion-micro hover:bg-soft"
            >
              <X className="size-6" aria-hidden="true" />
            </button>
          </div>

          <nav aria-label="Main" className="flex-1 overflow-y-auto px-5 py-2">
            <ul className="divide-y divide-line">
              {PRIMARY_NAV.map((item) => (
                <li key={item.href}>
                  <Link
                    href={item.href}
                    onClick={close}
                    className="flex min-h-13 items-center justify-between gap-3 rounded-btn py-3 text-base font-medium text-ink motion-micro hover:text-orange-hover"
                  >
                    {item.label}
                    {item.hasMenu ? (
                      <ChevronDown className="size-4 text-ink-muted" aria-hidden="true" />
                    ) : null}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <div className="shrink-0 space-y-3 border-t border-line px-5 py-5">
            {/* The announcement bar carries these on desktop, and it is
                hidden at this width — so the contractor-facing links would
                otherwise be unreachable on a phone entirely. */}
            <Link
              href={ACTIONS.forContractors.href}
              onClick={close}
              className="flex min-h-11 items-center justify-center rounded-btn text-[0.9375rem] font-medium text-ink motion-micro hover:bg-soft"
            >
              {ACTIONS.forContractors.label}
            </Link>
            <Link
              href={ACTIONS.myBusiness.href}
              onClick={close}
              className="flex min-h-11 items-center justify-center rounded-btn text-[0.9375rem] font-medium text-ink motion-micro hover:bg-soft"
            >
              {ACTIONS.myBusiness.label}
            </Link>
            <div className="flex justify-center">
              <AccountMenu onNavigate={close} />
            </div>
            <Link href={ACTIONS.listBusiness.href} onClick={close} className="btn-cta w-full">
              {ACTIONS.listBusiness.label}
            </Link>
          </div>
        </div>
      </div>
    </>
  )
}
